import { useFormField, Form, SubmitFormButton, SelectField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Popup, Header, Title, Body, Footer } from '@calendly/ui/components/popup';
import classNames from 'classnames';
import { useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MembershipRole, MembershipRoleDescription } from 'components/core/role';
import { DOMAIN_CONTROL } from 'constants/billing_features';
import { isAvailableFeatureSelector } from 'features/billing';
import { domainControlSettingLoadingSelector, getDomainControlSettingForOrg } from 'features/organization_settings/index';
import { isOwnerSelector } from 'selectors/current_user';
import { DomainControlNotice } from './domain_control_notice';
import { LoadingModal } from './loading_modal';
import { OwnershipWarningPopup } from './ownership_warning_popup';
import { changeRolePopupMembershipsSelector, isOwnershipWarningOpenSelector } from './selectors';
import { closeChangeRolePopup, openOwnershipWarningPopup, changeRoles } from './slice';
const initialRole = memberships => {
  if (memberships.count === 0) return null;
  const isSame = !memberships.some(value => value.role !== memberships[0].role);
  return isSame ? memberships[0].role : 'user';
};
const RoleDescription = props => {
  const [{
    value
  }] = useFormField({
    name: 'role'
  });
  return <span {...props}><MembershipRoleDescription membershipRole={value} /></span>;
};
const wrapperCss = "w1cma21i";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
export const ChangeRolePopup = () => {
  const dispatch = useDispatch();
  const onClose = () => dispatch(closeChangeRolePopup());
  const onConfirm = ({
    role
  }) => {
    const action = role === 'owner' ? openOwnershipWarningPopup() : changeRoles(role);
    return dispatch(action);
  };
  const memberships = useSelector(changeRolePopupMembershipsSelector);
  const isOwnershipWarningOpen = useSelector(isOwnershipWarningOpenSelector);
  const isOwner = useSelector(isOwnerSelector);
  const initialValues = {
    role: initialRole(memberships)
  };
  const isDomainControlFeatureAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: DOMAIN_CONTROL
  }));
  const domainControlSettingLoading = useSelector(domainControlSettingLoadingSelector);
  useEffect(() => {
    if (isDomainControlFeatureAvailable) {
      dispatch(getDomainControlSettingForOrg());
    }
  }, [dispatch, isDomainControlFeatureAvailable]);
  const options = ['user', 'admin', 'owner'].map(role => ({
    id: role,
    name: <MembershipRole membershipRole={role} />
  }));
  const filteredOptions = !isOwner || memberships.length > 1 ? options.filter(({
    id
  }) => id !== 'owner') : options;
  if (domainControlSettingLoading) {
    return <LoadingModal />;
  }
  if (isOwnershipWarningOpen) return <OwnershipWarningPopup />;
  return <Popup onClose={onClose}>
      <Form initialValues={initialValues} onSubmit={onConfirm}>
        <Wrapper>
          <Header>
            <Title id="roleLabel">{I18n.t('publisher.organization.members.change_role')}</Title>
          </Header>
          <Body>
            <Wrapper>
              <RoleDescription id="roleDescription" />
              <SelectField aria-labelledby="roleLabel" aria-describedby="roleDescription" name="role" options={filteredOptions} />
            </Wrapper>
          </Body>
          <Footer>
            <Button decoration="secondary-outline" onClick={onClose}>
              {I18n.t('publisher.action.cancel')}
            </Button>
            <SubmitFormButton>
              {I18n.t('publisher.action.apply')}
            </SubmitFormButton>
          </Footer>
          <DomainControlNotice memberships={memberships} remove={false} />
        </Wrapper>
      </Form>
    </Popup>;
};

require("./change_role_popup.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./change_role_popup.js");